import React, { useEffect, useState, createContext, useContext } from 'react';
import { useUserContext } from '../user_context/UserContext';
import { organizationSnapshotListener } from '../../repository/OrganizationDAO';
import { organizationEmployeesSnapshotListener } from '../../repository/EmployeeDAO';
import { organizationCustomersSnapshotListener } from '../../repository/ClientDAO'; 
import { organizationStripeInfoSnapshotListener } from '../../repository/OrganizationStripeDAO';
import { UserRole } from '../../utils/enums/UserRoles';
import { departmentsSnapshotListener } from '../../repository/DepartmentsDAO';
import { getOrgSettings } from '../../repository/StaticDocumentsDAO';
import { UserStatus } from '../../components/routes/routing_statuses/RoutingStatuses';

export const OrganizationContext = createContext();
export const OrganizationProvider = ({ children }) => {
    const { firebaseUser } = useUserContext();    
    const [organization, setOrganization] = useState(UserStatus.UNINITIALIZED);
    const [organizationDepartments, setOrganizationDepartments] = useState([]);
    const [organizationEmployees, setOrganizationEmployees] = useState([]);
    const [organizationCustomers, setOrganizationCustomers] = useState([]);
    const [organizationSettings, setOrganizationSettings] = useState(null);
    const [timeKeepingMethod, setTimeKeepingMethod] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    const [role, setRole] = useState();
    const [subscribedUsers, setSubscribedUsers] = useState({});
    const [organizationRoles, setOrganizationRoles] = useState({});
    const [organizationStripeInfo, setOrganizationStripeInfo] = useState(UserStatus.UNINITIALIZED);
    // Track subscription initialization state
    const [subscriptionInitialized, setSubscriptionInitialized] = useState(false);
    
    useEffect(() => {
      if (firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED && firebaseUser !== UserStatus.DOES_NOT_EXIST) {
        if (firebaseUser?.organizations?.[0]) {
          const unsubscribe = organizationSnapshotListener(firebaseUser.organizations[0], setOrganization);
          return () => unsubscribe();
        } else setOrganization(UserStatus.DOES_NOT_EXIST);
      } else setOrganization(UserStatus.UNINITIALIZED);
    }, [firebaseUser]);

    useEffect(() => {
      if (organization && organization !== UserStatus.UNINITIALIZED && organization !== UserStatus.DOES_NOT_EXIST) setOrganizationRoles(organization.roles);
      else setOrganizationRoles({});
    }, [organization]);

    useEffect(() => {
      if (firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED && firebaseUser !== UserStatus.DOES_NOT_EXIST && firebaseUser.organization) {
        const unsubscribe = departmentsSnapshotListener(firebaseUser.organization, setOrganizationDepartments);
        return () => unsubscribe();
      }
    }, [firebaseUser]);

    useEffect(() => {
      if (firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED && firebaseUser !== UserStatus.DOES_NOT_EXIST && firebaseUser.organization) {
        const unsubscribe = organizationEmployeesSnapshotListener(firebaseUser.organization, setOrganizationEmployees);
        return () => unsubscribe();
      }
    }, [firebaseUser]);

    useEffect(() => {
      if (firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED && firebaseUser !== UserStatus.DOES_NOT_EXIST && firebaseUser.organization) {
        const unsubscribe = organizationCustomersSnapshotListener(firebaseUser.organization, setOrganizationCustomers);
        return () => unsubscribe();
      }
    }, [firebaseUser]);

    useEffect(() => {
      if (firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED && firebaseUser !== UserStatus.DOES_NOT_EXIST && firebaseUser.organization) {
        const unsubscribe = organizationStripeInfoSnapshotListener(firebaseUser.organization, setOrganizationStripeInfo);
        return () => unsubscribe();
      }
    }, [firebaseUser]);

    useEffect(() => {
      if (firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED && firebaseUser !== UserStatus.DOES_NOT_EXIST && firebaseUser.organization) {
        (async () => {
          const orgSettings = await getOrgSettings(firebaseUser.organization);
          const settingsData = orgSettings.data();
          setOrganizationSettings(settingsData);
          setTimeKeepingMethod(settingsData.time_method);
        })();
      }
    }, [firebaseUser]);

    useEffect(() => {
      if (organization && firebaseUser && organization !== UserStatus.UNINITIALIZED && organization !== UserStatus.DOES_NOT_EXIST) {
        const userRole = organization.subscribed_users[firebaseUser.uid];
        if (userRole) {
          setRole(userRole);
          setIsOwner(userRole === UserRole.Owner);
        }
        setSubscribedUsers(organization.subscribed_users);
      }
    }, [organization, firebaseUser]);

    // Sync subscriptionInitialized state with Firebase when organization data loads
    useEffect(() => {
      if (organization && organization !== UserStatus.UNINITIALIZED && organization !== UserStatus.DOES_NOT_EXIST) {
        // If the Firebase flag is true, make sure the context state is also true
        if (organization.subscription_initialized === true && !subscriptionInitialized) {
          setSubscriptionInitialized(true);
        }
      }
    }, [organization, subscriptionInitialized]);

    const resetOrganizationContext = () => {
      setOrganization(UserStatus.UNINITIALIZED);
      setOrganizationDepartments([]);
      setOrganizationEmployees([]);
      setOrganizationCustomers([]);
      setOrganizationSettings(null);
      setTimeKeepingMethod(null);
      setIsOwner(false);
      setRole();
      setSubscribedUsers({});
      setOrganizationRoles({});
      setOrganizationStripeInfo(UserStatus.UNINITIALIZED);
      setSubscriptionInitialized(false);
    };

    // Mark subscription as initialized (completed setup)
    const markSubscriptionInitialized = () => {
      setSubscriptionInitialized(true);
    };

    // Reset subscription initialization state (for testing or reset)
    const resetSubscriptionInitialization = () => {
      setSubscriptionInitialized(false);
    };

    const getFilteredEmployees = (status, department, searchTerm = '', sortOrder = 'asc') => {
      let filteredEmployees = [...organizationEmployees];

      if (status) {
        filteredEmployees = filteredEmployees.filter(employee => 
          status === 'active' ? employee.active : !employee.active
        );
      }
      
      if (department) {
        filteredEmployees = filteredEmployees.filter(employee => employee.department_id === department);
      }
      
      if (searchTerm) {
        filteredEmployees = filteredEmployees.filter(employee =>
          employee.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.last_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      filteredEmployees.sort((a, b) => 
        sortOrder === 'asc' ? a.first_name.localeCompare(b.first_name) : b.first_name.localeCompare(a.first_name)
      );

      return filteredEmployees;
    };

    const getFilteredRoleByName = (searchTerm, sortOrder = 'asc') => {
      let filteredRoles = Object.keys(organizationRoles).map(roleName => ({
        name: roleName,
        permissions: organizationRoles[roleName]
      }));

      if (searchTerm) {
        filteredRoles = filteredRoles.filter(role => 
          role.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      filteredRoles.sort((a, b) => 
        sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      );

      return filteredRoles;
    };

    const getFilteredItem = (filterItem, searchTerm = '', sortOrder = 'asc') => {
      let filteredItems = [];

      if (filterItem === "departments") {
        filteredItems = [...organizationDepartments];
      }

      if (searchTerm) {
        filteredItems = filteredItems.filter(item =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      filteredItems.sort((a, b) => 
        sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      );

      return filteredItems;
    };

    const getCustomersSortedByLastName = () => {
        return [...organizationCustomers].sort((a, b) => {
            if (a.last_name < b.last_name) return -1;
            if (a.last_name > b.last_name) return 1;
            return 0;
        });
    };

    const getEmployeeByID = (employeeId) => {
      return organizationEmployees.find(emp => emp.id === employeeId);
    };

    const getCustomerByID = (customerId) => {
      return organizationCustomers.find(customer => customer.id === customerId);
    };

    const getDepartmentByID = (departmentId) => {
      return organizationDepartments.find(dept => dept.id === departmentId)
    }

    const getEmployeesByDepartment = (departmentId) => {
      return organizationEmployees.filter(emp => emp.department_id === departmentId) || [];
    };

    const isUserDepartmentManager = (userId) => {
      const employee = organizationEmployees.find(emp => emp.id === userId);
      if (!employee?.department_id) return false;
      
      const department = organizationDepartments.find(dept => dept.id === employee.department_id);
      return department?.employees?.[userId]?.is_manager || false;
    };

    return (
      <OrganizationContext.Provider
        value={{
          organization,
          role,
          isOwner,
          organizationCustomers,
          getCustomerByID,
          getCustomersSortedByLastName,
          organizationDepartments,
          organizationEmployees,
          getDepartmentByID,
          getEmployeesByDepartment,
          getFilteredEmployees,
          getEmployeeByID,
          getFilteredRoleByName,
          getFilteredItem,
          organizationSettings,
          organizationStripeInfo,
          timeKeepingMethod,
          subscribedUsers,
          organizationRoles,
          resetOrganizationContext,
          isUserDepartmentManager,
          subscriptionInitialized,
          markSubscriptionInitialized,
          resetSubscriptionInitialization
        }}
      >
        {children}
      </OrganizationContext.Provider>
    );
};

export const useOrganizationContext = () => useContext(OrganizationContext);
